/* Arrow paging for the collection carousel.
 *
 * The carousel itself is a native scroll-snap scroller, so the arrows do not own an
 * index — they nudge `scrollLeft` by one card and let scroll snapping land the result.
 * That keeps the arrows purely additive: touch swipe, trackpad scroll and keyboard
 * scrolling all keep working, and the arrow state is derived from the scroll position
 * rather than from a counter that could drift out of sync with them.
 *
 * Guarded define: two of these sections on one page emit the tag twice and both execute,
 * and a bare `customElements.define` would throw on the second. */
if (!customElements.get('collection-carousel')) {
  customElements.define('collection-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.scroller = this.querySelector('.collection-carousel__scroller');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      if (!this.scroller || !this.prevButton) return;

      this.prevButton.addEventListener('click', () => this.page(-1));
      this.nextButton.addEventListener('click', () => this.page(1));
      this.scroller.addEventListener('scroll', () => this.updateArrows(), { passive: true });

      /* Card width is fluid (a share of the content width at desktop, viewport units
         below), so the reachable scroll range changes on resize and with it whether
         either end has been hit. */
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.updateArrows());
        this.observer.observe(this.scroller);
      } else {
        window.addEventListener('resize', () => this.updateArrows());
      }

      this.updateArrows();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /* One card per press, measured from the first two cards' offset delta so the grid
       gap is included without having to read it back off the computed style. */
    page(direction) {
      const cards = this.scroller.querySelectorAll('.collection-card');
      if (cards.length < 2) return;
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      this.scroller.scrollBy({
        left: direction * step,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    updateArrows() {
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      /* 1px of slack: fractional layout widths mean the far end is routinely a
         sub-pixel short of the arithmetic maximum and would never register as reached. */
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;
      /* Nothing to page through at all — hide the pair rather than show two dead
         controls, which is what the reference does when a slider fits its view. */
      const idle = scrollWidth <= clientWidth + 1;

      this.classList.toggle('collection-carousel--pageable', !idle);
      this.prevButton.disabled = atStart;
      this.nextButton.disabled = atEnd;
      /* Disabled arrows fade out entirely (see the stylesheet), so they must also leave
         the tab order — a focusable control the user cannot see is a keyboard trap. */
      this.prevButton.tabIndex = atStart ? -1 : 0;
      this.nextButton.tabIndex = atEnd ? -1 : 0;
    }
  });
}
